import React from 'react';
import { Checkbox, Dropdown, Tag } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { pickPageName, pickPortalName } from '../../../../utils/dataPortalCache';
import { DataPortalPageType } from '../graphql/queries';

interface Props {
    page: DataPortalPageType;
    selected: boolean;
    onToggleSelect: (id: string) => void;
    onEdit: (page: DataPortalPageType) => void;
    onMove: (page: DataPortalPageType) => void;
    onDelete: (page: DataPortalPageType) => void;
}

/**
 * 看板卡片。
 *
 * 副信息按交接文档：`看板ID · 来源门户 · 操作人 · 排序 N`。
 * 「看板 ID」就是 `pageKey`（前端路由 `/data-portal/:pageKey` 的标识），
 * 原来叫「页面标识」，业务方不理解，本期统一改称看板 ID。
 *
 * 启用/停用只在卡片上展示状态，不可点切换：启停统一在编辑弹窗的「启用」开关里做。
 */
const BoardCard: React.FC<Props> = ({
    page,
    selected,
    onToggleSelect,
    onEdit,
    onMove,
    onDelete,
}) => {
    const { t, i18n } = useTranslation();
    const name = pickPageName(page, i18n.language);
    const operator = page.updatedBy || page.createdBy || '-';

    return (
        <div
            className={`relative bg-white rounded-lg border p-4 flex flex-col gap-2 ${
                page.groupId ? 'border-gray-200' : 'border-amber-200'
            }`}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                    <Checkbox checked={selected} onChange={() => onToggleSelect(page.id)} />
                    <span className="font-medium truncate" title={name}>
                        {page.icon ? `${page.icon} ${name}` : name}
                    </span>
                </div>
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: [
                            { key: 'edit', label: t('components.button.edit') },
                            { key: 'move', label: t('central.dataPortalConfig.actions.moveToGroup') },
                            { key: 'delete', label: t('components.button.delete'), danger: true },
                        ],
                        onClick: ({ key }) => {
                            if (key === 'edit') onEdit(page);
                            if (key === 'move') onMove(page);
                            if (key === 'delete') onDelete(page);
                        },
                    }}
                >
                    <MoreOutlined className="cursor-pointer text-gray-400 flex-none" />
                </Dropdown>
            </div>

            <div className="text-xs text-gray-400 truncate">
                {page.pageKey}
                {' · '}
                {pickPortalName(page.portalCode, t)}
                {' · '}
                {operator}
                {' · '}
                {t('central.dataPortalConfig.fields.sortOrder')} {page.sortOrder}
            </div>

            <div className="flex items-center gap-2">
                {/* 启用状态只展示、不可点切换：启停统一在编辑弹窗的「启用」开关里做，
                    避免卡片上误点直接改变线上可见性 */}
                <Tag color={page.enabled ? 'green' : 'default'}>
                    {page.enabled ? t('enum.activeStatus.active') : t('enum.activeStatus.suspended')}
                </Tag>
                <Tag>
                    {page.visibleScope === 'ALL'
                        ? t('central.dataPortalConfig.visibleScope.all')
                        : page.visibleScope === 'ACCOUNT'
                          ? page.visibleAccounts.length === 0
                              ? t('central.dataPortalConfig.restrictedOnlyAdmin')
                              : t('central.dataPortalConfig.visibleCount', { count: page.visibleAccounts.length })
                          : page.visibleRoles.length === 0
                            ? t('central.dataPortalConfig.restrictedOnlyAdmin')
                            : t('central.dataPortalConfig.visibleRoleCount', { count: page.visibleRoles.length })}
                </Tag>
            </div>
        </div>
    );
};

export default BoardCard;
